import { Fragment, useEffect, useRef, useState } from "react";
import Matter from "matter-js";
import { getI18nLanguageFromLocalStorage } from "../helpers/localStorageHelper";
import { i18n, initializeI18n } from "../localization/i18n";
import { translate } from "../localization/translateService";

const HERO_TITLE_FALLBACK = "Korea’s Representative Education Platform";

// Pastel pill colors (fallback when a CMS pill has no color)
// Figma hero pill palette (used when a CMS pill has no explicit color)
const PILL_COLORS_FALLBACK = [

  "#FFD817", "#3054F1", "#F3F2F7", "#47CDF2", "#FBA935",
];

// Watermark word per language (DOTORE teaches Korean)
const WATERMARK_BY_LANG = { ko: "KOREAN", en: "ENGLISH", vi: "KOREAN" };

// Physics tuning — calmer motion: smaller scroll "kick" + less bounce so pills
// drift gently instead of jumping around.
const WALL = 200;          // wall thickness (prevents tunneling)
const KICK_UP = 0.007;     // base upward impulse on scroll (was 0.016)
const KICK_VAR = 0.009;    // extra upward impulse scaled by scroll speed (was 0.02)
const KICK_SPREAD = 0.003; // small random horizontal jitter (was 0.006)
const MAX_TILT = 0.45;     // max pill rotation (~26deg) so text stays readable
const IMG_PILL_SIZE = 84;  // default square size for image pills (px)

export default function HeroSection({ props }) {
  const currentLanguage = getI18nLanguageFromLocalStorage();
  const pills = props?.heroPills || [];
  const mascot = props?.heroMascot;                 // image URL (optional)
  const mascotSize = props?.heroMascotSize || 110;  // px
  const hasMascot = !!mascot;

  const containerRef = useRef(null);
  const pillRefs = useRef([]);

  // Watermark is CMS-driven only (no hardcoded fallback); empty -> not shown (matches Figma).
  const watermark = props?.heroWatermark;

  // Hero title -> i18n (varies by language). Falls back to the CMS block value, then hardcode.
  const [heroTitle, setHeroTitle] = useState(props?.heroTitle || HERO_TITLE_FALLBACK);
  useEffect(() => {
    let active = true;
    (async () => {
      if (!i18n.isInitialized) await initializeI18n();
      const v = await translate("UMBRACO_LANDING.HOME.HERO.TITLE", false, props?.heroTitle || HERO_TITLE_FALLBACK);
      if (active) setHeroTitle(v);
    })();
    return () => { active = false; };
  }, []);

  const badges = [
    { label: props?.heroBadge1Label, link: props?.heroBadge1Link },
    { label: props?.heroBadge2Label, link: props?.heroBadge2Link },
    { label: props?.heroBadge3Label, link: props?.heroBadge3Link },
  ].filter((b) => b.label);

  const handleCtaClick = () => {
    const link = props?.heroCtaButtonLink || `/${currentLanguage}/pretest`;
    window.location.href = link;
  };

  // Single CMS-driven hero media frame: YouTube/Vimeo embed, mp4 <video>, or poster image.
  const heroVideoUrl = props?.heroVideoUrl;
  const heroPoster = props?.heroImage1;
  const renderHeroMedia = () => {
    if (heroVideoUrl) {
      const u = String(heroVideoUrl).trim();
      const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
      if (yt) return (
        <iframe className="hero-video__frame" src={`https://www.youtube.com/embed/${yt[1]}?autoplay=1&mute=1&loop=1&playlist=${yt[1]}&controls=0&modestbranding=1&rel=0&playsinline=1`}
          title="hero video" frameBorder="0" allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
      );
      const vm = u.match(/vimeo\.com\/(\d+)/);
      if (vm) return (
        <iframe className="hero-video__frame" src={`https://player.vimeo.com/video/${vm[1]}?autoplay=1&muted=1&loop=1&background=1`}
          title="hero video" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
      );
      return <video className="hero-video__frame" src={u} poster={heroPoster || undefined} autoPlay loop muted playsInline />;
    }
    if (heroPoster) return <img className="hero-video__frame" src={heroPoster} alt="" />;
    return null;
  };

  // Matter.js: pills (and an optional mascot) drop inside the box; ONLY scrolling
  // kicks them up. No mouse/hover/drag interaction. Enclosed -> nothing leaves the box.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || (pills.length === 0 && !hasMascot)) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const { Engine, Runner, Bodies, Composite, Body, Events } = Matter;

    let W = container.clientWidth;
    let H = container.clientHeight;
    const isMobile = W <= 768;

    const engine = Engine.create();
    engine.gravity.y = reduceMotion ? 0 : 1;

    // Closed box: floor + left + right + ceiling (edges flush with the container)
    const walls = [
      Bodies.rectangle(W / 2, H + WALL / 2, W + WALL * 2, WALL, { isStatic: true }),
      Bodies.rectangle(-WALL / 2, H / 2, WALL, H + WALL * 2, { isStatic: true }),
      Bodies.rectangle(W + WALL / 2, H / 2, WALL, H + WALL * 2, { isStatic: true }),
      Bodies.rectangle(W / 2, -WALL / 2, W + WALL * 2, WALL, { isStatic: true }),
    ];

    // Entities = pills (rectangles) + optional mascot (circle, stays upright)
    const count = pills.length + (hasMascot ? 1 : 0);
    const isMascotIndex = (i) => hasMascot && i === pills.length;

    let mobileLayout = null;
    if (isMobile) {
      const GAP = 16;
      const SIDE = 14;
      const usableW = Math.max(60, W - SIDE * 2);
      const widths = [];
      for (let k = 0; k < count; k++) {
        const elK = pillRefs.current[k];
        widths.push(elK ? elK.offsetWidth : 100);
      }
      const rowsIdx = [];
      let rowAcc = [];
      let rowW = 0;
      for (let k = 0; k < count; k++) {
        const wK = widths[k];
        const added = rowAcc.length ? rowW + GAP + wK : wK;
        if (added > usableW && rowAcc.length) { rowsIdx.push(rowAcc); rowAcc = []; rowW = 0; }
        rowAcc.push(k);
        rowW = rowAcc.length > 1 ? rowW + GAP + wK : wK;
      }
      if (rowAcc.length) rowsIdx.push(rowAcc);

      const centers = new Array(count);
      rowsIdx.forEach((idxs, r) => {
        const total = idxs.reduce((s, k, n) => s + widths[k] + (n ? GAP : 0), 0);
        const avgW = total / idxs.length;
        const stagger = (r % 2 === 0 ? -0.25 : 0.25) * avgW;
        let cursor = SIDE + (usableW - total) / 2 + stagger;
        idxs.forEach((k) => {
          centers[k] = { row: r, cx: cursor + widths[k] / 2 };
          cursor += widths[k] + GAP;
        });
      });
      mobileLayout = { centers, rowCount: rowsIdx.length };
    }

    const sizes = [];
    const bodies = [];
    for (let i = 0; i < count; i++) {
      const el = pillRefs.current[i];
      const w = el ? el.offsetWidth : 100;
      const h = el ? el.offsetHeight : 50;
      sizes.push({ w, h });

      let x, y;
      if (isMobile) {
        const { row, cx } = mobileLayout.centers[i];
        const rc = mobileLayout.rowCount;
        const bandTop = h / 2 + 10;
        const bandH = Math.max(0, H - h - 20);
        const rowY = rc > 1 ? bandTop + (row / (rc - 1)) * bandH : bandTop + bandH / 2;
        x = cx + (Math.random() - 0.5) * 12;
        y = reduceMotion
          ? H - h / 2 - i * (h + 8)
          : rowY + (Math.random() - 0.5) * Math.min(bandH / rc, 24);
      } else {
        x = ((i + 1) / (count + 1)) * W + (Math.random() - 0.5) * 40;
        y = reduceMotion ? H - h / 2 - i * (h + 8) : h / 2 + 10 + Math.random() * (H * 0.3);
      }

      if (isMascotIndex(i)) {
        bodies.push(
          Bodies.circle(x, y, Math.max(w, h) / 2, {
            restitution: 0.2,
            friction: 0.4,
            frictionAir: 0.05,
          })
        );
      } else {
        bodies.push(
          Bodies.rectangle(x, y, w, h, {
            chamfer: { radius: h / 2 },
            restitution: 0.2,
            friction: 0.4,
            frictionAir: 0.05,
            angle: isMobile ? Math.random() * Math.PI * 2 : (Math.random() - 0.5) * 0.3,
          })
        );
      }
    }

    Composite.add(engine.world, [...walls, ...bodies]);

    // Kick every body upward; strength 0..1 scales the lift
    const kick = (strength) => {
      bodies.forEach((b) => {
        Body.applyForce(b, b.position, {
          x: (Math.random() - 0.5) * KICK_SPREAD * b.mass,
          y: -(KICK_UP + strength * KICK_VAR) * b.mass,
        });
      });
    };

    // Trigger ONLY on scroll (wheel/trackpad/keys) — not on mouse move
    let lastY = window.scrollY || window.pageYOffset || 0;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset || 0;
        const strength = Math.min(Math.abs(y - lastY), 60) / 60;
        lastY = y;
        kick(strength);
        ticking = false;
      });
    };
    if (!reduceMotion) window.addEventListener("scroll", onScroll, { passive: true });

    // Sync DOM transforms to bodies + clamp inside the box / limit tilt
    const sync = () => {
      bodies.forEach((b, i) => {
        const el = pillRefs.current[i];
        if (!el) return;
        const { w, h } = sizes[i];

        const cx = Math.min(W - w / 2, Math.max(w / 2, b.position.x));
        const cy = Math.min(H - h / 2, Math.max(h / 2, b.position.y));
        if (cx !== b.position.x || cy !== b.position.y) Body.setPosition(b, { x: cx, y: cy });

        let angle = b.angle;
        if (isMascotIndex(i)) {
          angle = 0; // keep mascot upright
        } else if (!isMobile) {
          if (angle > MAX_TILT) { Body.setAngle(b, MAX_TILT); Body.setAngularVelocity(b, 0); angle = MAX_TILT; }
          else if (angle < -MAX_TILT) { Body.setAngle(b, -MAX_TILT); Body.setAngularVelocity(b, 0); angle = -MAX_TILT; }
        }

        el.style.transform =
          `translate(${b.position.x - w / 2}px, ${b.position.y - h / 2}px) rotate(${angle}rad)`;
        if (!el.classList.contains("is-ready")) el.classList.add("is-ready");
      });
    };
    Events.on(engine, "afterUpdate", sync);
    sync();

    const runner = Runner.create();
    Runner.run(runner, engine);

    // Keep the walls flush with the container's REAL size. Reading the height only
    // once at mount leaves a stale floor if the layout reflows (i18n/fonts/video
    // load), which lets pills fall past the intended boundary — so re-measure on
    // both window resize AND any container size change (ResizeObserver).
    const onResize = () => {
      W = container.clientWidth;
      H = container.clientHeight;
      Body.setPosition(walls[0], { x: W / 2, y: H + WALL / 2 });   // floor
      Body.setPosition(walls[1], { x: -WALL / 2, y: H / 2 });       // left
      Body.setPosition(walls[2], { x: W + WALL / 2, y: H / 2 });    // right
      Body.setPosition(walls[3], { x: W / 2, y: -WALL / 2 });       // ceiling
    };
    window.addEventListener("resize", onResize);
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => onResize());
      ro.observe(container);
    }

    return () => {
      Events.off(engine, "afterUpdate", sync);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
      Runner.stop(runner);
      Composite.clear(engine.world, false);
      Engine.clear(engine);
    };
  }, [pills.length, hasMascot, mascotSize]);

  return (
    <div className="hero-section-wrapper hero-tall">
      {/* Background watermark */}
      {watermark && <div className="hero-watermark" aria-hidden="true">{watermark}</div>}

      {/* Top: title + highlighted tags + image cards (Figma layout) */}
      <div className="hero-top">
        <h1 className="hero-main-title animate-on-scroll">
          {/* Honor CMS-authored line breaks in the title (per-language wrapping):
              a "<br>" marker or a real newline in the translated value splits it
              across lines. No marker -> single line, unchanged. */}
          {String(heroTitle).split(/\s*<br\s*\/?>\s*|\r?\n/i).map((line, i) => (
            <Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </Fragment>
          ))}
        </h1>

        {props?.heroSubtitle && (
          <p className="hero-subtitle animate-on-scroll">{props.heroSubtitle}</p>
        )}

        <div className="hero-video animate-on-scroll">
          {renderHeroMedia()}
        </div>

        {badges.length > 0 && (
          <div className="hero-badges animate-on-scroll">
            {badges.map((badge, i) => (
              <a key={i} href={badge.link || "#"} className="hero-badge-chip">
                {badge.label}
              </a>
            ))}
          </div>
        )}

        {props?.heroCtaButtonLabel && (
          <button className="hero-cta-btn animate-on-scroll" onClick={handleCtaClick}>
            {props.heroCtaButtonLabel}
          </button>
        )}
      </div>

      {/* Physics pills band (bottom) */}
      <div className="hero-pills-container" ref={containerRef} aria-hidden="true">
        {pills.map((pill, i) => {
          const bg = pill.color || PILL_COLORS_FALLBACK[i % PILL_COLORS_FALLBACK.length];
          // Text is black by default; only blue (#3054F1) pills get white text.
          const isBluePill =
            !pill.image && typeof bg === "string" && bg.trim().toUpperCase() === "#3054F1";
          return (
            <div
              key={i}
              ref={(el) => (pillRefs.current[i] = el)}
              className={"hero-floating-pill" + (pill.image ? " has-img" : "")}
              style={
                pill.image
                  ? {
                      width: (pill.width || IMG_PILL_SIZE) + "px",
                      height: (pill.height || IMG_PILL_SIZE) + "px",
                    }
                  : { background: bg, color: isBluePill ? "#fff" : "#000" }
              }
            >
              {pill.image ? (
                <img className="hero-pill-img" src={pill.image} alt={pill.label || ""} />
              ) : (
                pill.label
              )}
            </div>
          );
        })}

        {hasMascot && (
          <img
            ref={(el) => (pillRefs.current[pills.length] = el)}
            className="hero-mascot"
            src={mascot}
            alt=""
            style={{ width: mascotSize + "px", height: mascotSize + "px" }}
          />
        )}
      </div>
    </div>
  );
}
